import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    RequestFn, RequestKeys,
} from 'app/hooks/useRequest'
import {
    RequestConfigFunction,
} from 'app/types/request.types'
import sleep from 'app/utils/sleep'

import usePaginatedRequest from 'app/hooks/usePaginatedRequest'
import {
    TableFilterParams,
} from 'app/types/common.types'
import {
    WAIT_BETWEEN_REQUESTS_IN_SERIES,
} from 'app/shared-components/BatchActions/BatchActions.utils'

const PAGE_SIZE = 200

type Props = {
    request: RequestConfigFunction<Record<string, any>>,
    requestKey: RequestKeys
    filterParams: TableFilterParams,
}

const useFetchBulkData = ({
    request,
    requestKey,
    filterParams,
}: Readonly<Props>) => {
    const [
        hasStarted,
        setHasStarted,
    ] = useState(false)

    const [
        isCancelled,
        setIsCancelled,
    ] = useState(false)

    const {
        data,
        hasNext,
        fetchNext,
        isFetching,
    } = usePaginatedRequest({
        requestFunc: RequestFn.getEntity(
            (payload) => {
                return {
                    ...payload,
                    ...request(payload),
                }
            },
        ),
        key: requestKey,
        rows: PAGE_SIZE,
        params: {
            data: filterParams,
        },
        enabled: !isCancelled,
    })

    const loadNextPage = useCallback(async () => {
        await sleep(WAIT_BETWEEN_REQUESTS_IN_SERIES)
        fetchNext()
    }, [fetchNext])

    useEffect(() => {
        if (hasNext && !isFetching) {
            loadNextPage()
        }
    }, [
        hasNext,
        isFetching,
        loadNextPage,
    ])

    useEffect(() => {
        if (isFetching && !hasStarted) {
            setHasStarted(true)
        }
    }, [
        isFetching,
        hasStarted,
    ])

    const handleCancel = useCallback(() => {
        setIsCancelled(true)
    }, [])

    const isComplete = hasStarted && !hasNext && hasNext !== undefined

    const memoizedData = useMemo(() => {
        return isComplete ? data : null
    }, [
        data,
        isComplete,
    ])

    return {
        cancel: handleCancel,
        data: memoizedData,
        loading: !isComplete,
        isComplete,
    }
}

export default useFetchBulkData
